import { useMemo, useRef, useState } from 'react';
import { useVault } from '../lib/vault';
import { api } from '../lib/api';
import type { PermissionKey, PublicUser } from '@shared/types';
import { PERMISSION_HINT, PERMISSION_KEYS, PERMISSION_LABEL } from '@shared/types';
import { Avatar, Confirm, ErrorNote, Modal, useAction } from '../components/ui';
import { toAvatarDataUrl } from '../lib/image';
import { plural } from '@shared/text';
import type { View } from '../app';

export function TeamPage({
  onNavigate,
}: {
  onNavigate: (view: View, ids?: string[]) => void;
}): JSX.Element {
  const { users, items, me, isAdmin, can, reload, regrantAll } = useVault();
  const { busy, error, setError, run } = useAction();
  const [confirmDelete, setConfirmDelete] = useState<PublicUser | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Wessen Rechte gerade offen sind. */
  const [rightsFor, setRightsFor] = useState<PublicUser | null>(null);
  const darfVerwalten = can('manageTeam');

  const accessCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      for (const uid of item.accessUids) counts.set(uid, (counts.get(uid) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const ownerCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (item.ownerUid) counts.set(item.ownerUid, (counts.get(item.ownerUid) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const pendingRegrant = users.filter((u) => u.needsRegrant && u.publicKey && u.uid !== me?.uid);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Team</h1>
        </div>
      </div>

      <ErrorNote error={error} />
      {notice ? <div className="note note-ok">{notice}</div> : null}

      {pendingRegrant.length > 0 ? (
        <div className="note note-warn" style={{ marginBottom: 14 }}>
          <strong>
            {plural(pendingRegrant.length, 'Person hat', 'Personen haben')} den Schlüssel
            zurückgesetzt und wartet auf die Zugänge.
          </strong>
          <div className="stack" style={{ marginTop: 10 }}>
            {pendingRegrant.map((u) => (
              <div key={u.uid} className="row">
                <span>{u.name}</span>
                <span className="spacer" />
                <button
                  className="btn-sm"
                  disabled={busy}
                  onClick={async () => {
                    const applied = await run(() => regrantAll(u.uid));
                    if (applied !== undefined) {
                      setNotice(`${applied} Zugänge an ${u.name} zurückgegeben.`);
                    }
                  }}
                >
                  Meine Zugänge zurückgeben
                </button>
              </div>
            ))}
          </div>
          <p className="small" style={{ margin: '10px 0 0' }}>
            Zurückgegeben wird nur, was du selbst entschlüsseln kannst. Für den Rest muss jemand
            anderes dasselbe tun.
          </p>
        </div>
      ) : null}

      <div className="card">
        <table className="stacked-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Rolle</th>
              <th className="num col-optional">Zugänge</th>
              <th className="num col-optional">Verträge</th>
              <th>Status</th>
              {darfVerwalten ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.uid}>
                <td>
                  <div className="row-tight">
                    <Avatar user={u} size={32} />
                    <div>
                      <div className="row-tight">
                        <strong>{u.name}</strong>
                        {u.uid === me?.uid ? <span className="badge">du</span> : null}
                      </div>
                      <div className="small muted">{u.email}</div>
                      {u.uid === me?.uid ? (
                        <PictureButtons
                          hasPicture={Boolean(u.picture)}
                          busy={busy}
                          onPick={async (file) => {
                            await run(async () => {
                              await api.setOwnPicture(await toAvatarDataUrl(file));
                              await reload();
                            });
                          }}
                          onClear={async () => {
                            await run(async () => {
                              await api.setOwnPicture(null);
                              await reload();
                            });
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                </td>
                <td>
                  {isAdmin && u.uid !== me?.uid ? (
                    <select
                      value={u.role}
                      disabled={busy}
                      onChange={async (e) => {
                        await run(async () => {
                          await api.updateUser(u.uid, {
                            role: e.target.value as 'admin' | 'member',
                          });
                          await reload();
                        });
                      }}
                      style={{ maxWidth: 130 }}
                    >
                      <option value="member">Mitglied</option>
                      <option value="admin">Admin</option>
                    </select>
                  ) : (
                    <span className="small">{u.role === 'admin' ? 'Admin' : 'Mitglied'}</span>
                  )}
                </td>
                <td className="num mono col-optional" data-label="Zugänge">
                  {accessCount.get(u.uid) ?? 0}
                </td>
                <td className="num mono col-optional" data-label="Verträge">
                  {ownerCount.get(u.uid) ?? 0}
                </td>
                <td>
                  {u.status === 'disabled' ? (
                    <span className="badge badge-danger">deaktiviert</span>
                  ) : !u.hasVaultKey ? (
                    <span className="badge badge-warn">keine Passphrase</span>
                  ) : u.needsRegrant ? (
                    <span className="badge badge-warn">wartet auf Zugänge</span>
                  ) : (
                    <span className="badge badge-ok">aktiv</span>
                  )}
                </td>
                {darfVerwalten ? (
                  <td className="num">
                    {u.uid === me?.uid ? null : (
                      <div className="row-tight" style={{ justifyContent: 'flex-end' }}>
                        {u.role === 'admin' ? null : (
                          <button className="btn-quiet btn-sm" onClick={() => setRightsFor(u)}>
                            Rechte
                          </button>
                        )}
                        {/* Ein- und Austritt gehören zu einer Person, nicht zu
                            einem eigenen Menüpunkt - deshalb starten sie hier. */}
                        <button
                          className="btn-quiet btn-sm"
                          onClick={() => onNavigate('onboarding', [u.uid])}
                        >
                          Einarbeiten
                        </button>
                        <button
                          className="btn-quiet btn-sm"
                          onClick={() => onNavigate('offboarding', [u.uid])}
                        >
                          Austritt
                        </button>
                        <button
                          className="btn-quiet btn-sm"
                          disabled={busy}
                          onClick={async () => {
                            await run(async () => {
                              await api.updateUser(u.uid, {
                                status: u.status === 'active' ? 'disabled' : 'active',
                              });
                              await reload();
                            });
                          }}
                        >
                          {u.status === 'active' ? 'Sperren' : 'Entsperren'}
                        </button>
                        <button
                          className="btn-quiet btn-sm"
                          disabled={busy}
                          onClick={() => {
                            setError(null);
                            setConfirmDelete(u);
                          }}
                        >
                          Löschen
                        </button>
                      </div>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rightsFor ? (
        <Modal
          wide
          title={`Rechte für ${rightsFor.name}`}
          subtitle="Gilt sofort. Was hier nicht angehakt ist, gibt der Server auch nicht heraus."
          onClose={() => setRightsFor(null)}
          footer={
            <button className="btn-primary" onClick={() => setRightsFor(null)}>
              Fertig
            </button>
          }
        >
          <RightsEditor
            user={users.find((u) => u.uid === rightsFor.uid) ?? rightsFor}
            busy={busy}
            onChange={async (key, value) => {
              await run(async () => {
                await api.updateUser(rightsFor.uid, { permissions: { [key]: value } });
                await reload();
              });
            }}
          />
        </Modal>
      ) : null}

      {confirmDelete ? (
        <Confirm
          danger
          title={`${confirmDelete.name} löschen?`}
          confirmLabel="Konto löschen"
          message={
            <>
              <p>
                Das Konto wird entfernt und alle {accessCount.get(confirmDelete.uid) ?? 0} Freigaben
                werden gelöscht. Betroffene Einträge werden zum Passwortwechsel markiert.
              </p>
              <p className="muted">
                Für einen geordneten Austritt ist der Weg über <strong>Offboarding</strong> besser –
                dort bekommst du eine Checkliste statt nur eines Löschvorgangs.
              </p>
            </>
          }
          onConfirm={async () => {
            await api.deleteUser(confirmDelete.uid);
            await reload();
          }}
          onClose={() => setConfirmDelete(null)}
        />
      ) : null}
    </>
  );
}


/** Bild wählen oder entfernen - nur für das eigene Konto. */
function PictureButtons({
  hasPicture,
  busy,
  onPick,
  onClear,
}: {
  hasPicture: boolean;
  busy: boolean;
  onPick: (file: File) => Promise<void>;
  onClear: () => Promise<void>;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="row-tight" style={{ marginTop: 4 }}>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) await onPick(file);
        }}
      />
      <button className="btn-quiet btn-sm" disabled={busy} onClick={() => input.current?.click()}>
        {hasPicture ? 'Bild ändern' : 'Bild hochladen'}
      </button>
      {hasPicture ? (
        <button className="btn-quiet btn-sm" disabled={busy} onClick={onClear}>
          Entfernen
        </button>
      ) : null}
    </div>
  );
}

/**
 * Die Rechte einer Person.
 *
 * Administratoren tauchen hier nicht auf: sie haben ohnehin alles, und ein
 * Häkchen, das sich nicht auswirkt, führt nur in die Irre. Wer weniger können
 * soll, wird erst zum Mitglied gemacht und bekommt dann einzelne Rechte.
 */
function RightsEditor({
  user,
  busy,
  onChange,
}: {
  user: PublicUser;
  busy: boolean;
  onChange: (key: PermissionKey, value: boolean) => Promise<void>;
}): JSX.Element {
  return (
    <div className="rights-grid">
      {PERMISSION_KEYS.map((key) => {
        const an = user.permissions[key];
        // Manche Rechte ziehen andere nach sich: „Preise bearbeiten" schließt
        // das Sehen ein, und Preise ohne die zugehörigen Zugänge wären eine
        // Liste von Beträgen ohne Gegenstand. Der Server setzt das ohnehin
        // mit - hier steht es nur ausgegraut da, damit es niemanden wundert.
        const erzwungenDurch =
          key === 'viewCosts' && user.permissions.manageCosts
            ? 'Preise bearbeitet werden dürfen'
            : key === 'viewAllItems' && user.permissions.viewCosts
              ? 'Preise sichtbar sind'
              : null;
        const erzwungen = erzwungenDurch !== null;
        return (
          <label key={key} className={`rights-item${erzwungen ? ' is-forced' : ''}`}>
            <input
              type="checkbox"
              checked={an}
              disabled={busy || erzwungen}
              onChange={(e) => void onChange(key, e.target.checked)}
            />
            <span>
              <strong>{PERMISSION_LABEL[key]}</strong>
              <span className="small muted">
                {erzwungenDurch ? `gilt automatisch, weil ${erzwungenDurch}` : PERMISSION_HINT[key]}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
